// frontend/src/pages/PerfilPage.jsx

import React, { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AuthContext from '../context/AuthContext';
import { User, Mail, Lock, CreditCard, Save, Users, Calendar, Activity, TrendingUp, ImagePlus, X, Building2 } from 'lucide-react';

export default function PerfilPage() {
  const { user: authUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [identidade, setIdentidade] = useState({ clinicName: '', clinicPhone: '', clinicEmail: '', logoAtiva: null });
  const [logos, setLogos] = useState({ logo1Url: null, logo2Url: null });
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    email: '',
    crefito: '',
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: '',
  });

  useEffect(() => {
    const buscarPerfil = async () => {
      try {
        const response = await api.get('/auth/me');
        const { nome, email, crefito, clinicName, clinicPhone, clinicEmail, logo1Url, logo2Url, logoAtiva } = response.data.data;
        setForm(prev => ({ ...prev, nome: nome || '', email: email || '', crefito: crefito || '' }));
        setIdentidade({ clinicName: clinicName || '', clinicPhone: clinicPhone || '', clinicEmail: clinicEmail || '', logoAtiva: logoAtiva || null });
        setLogos({ logo1Url: logo1Url || null, logo2Url: logo2Url || null });
      } catch {
        toast.error('Erro ao carregar perfil');
      }
    };

    const buscarStats = async () => {
      try {
        const [patientsRes, sessionsRes] = await Promise.all([
          api.get('/patients'),
          api.get('/sessions'),
        ]);
        const patients = patientsRes.data.data || [];
        const sessions = sessionsRes.data.data || [];

        const sessoesComEvolucao = sessions.filter(s => s.hasEvolution).length;

        setStats({
          totalPacientes: patients.length,
          totalSessoes: sessions.length,
          sessoesComEvolucao,
          sessoesAgendadas: sessions.filter(s => s.sessionDate).length,
        });
      } catch {
        setStats({ totalPacientes: 0, totalSessoes: 0, sessoesComEvolucao: 0, sessoesAgendadas: 0 });
      }
    };

    buscarPerfil();
    buscarStats();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSalvarDados = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/auth/perfil', { nome: form.nome, crefito: form.crefito });
      toast.success('Dados atualizados com sucesso!');
    } catch {
      toast.error('Erro ao atualizar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarSenha = async (e) => {
    e.preventDefault();
    if (form.novaSenha !== form.confirmarSenha) return toast.error('As senhas não conferem');
    if (form.novaSenha.length < 6) return toast.error('A nova senha deve ter pelo menos 6 caracteres');
    setLoading(true);
    try {
      await api.put('/auth/perfil', { senhaAtual: form.senhaAtual, novaSenha: form.novaSenha });
      toast.success('Senha alterada com sucesso!');
      setForm(prev => ({ ...prev, senhaAtual: '', novaSenha: '', confirmarSenha: '' }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Senha atual incorreta');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarIdentidade = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/auth/identidade', identidade);
      toast.success('Identidade visual salva!');
    } catch {
      toast.error('Erro ao salvar identidade');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLogo = async (slot, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Imagem muito grande. Máximo 2MB.');
    setUploadingSlot(slot);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post(`/auth/upload-logo/${slot}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogos(prev => ({ ...prev, [`logo${slot}Url`]: res.data.url }));
      toast.success(`Logo ${slot} salva!`);
    } catch {
      toast.error('Erro ao fazer upload da logo');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleDeleteLogo = async (slot) => {
    try {
      await api.delete(`/auth/upload-logo/${slot}`);
      setLogos(prev => ({ ...prev, [`logo${slot}Url`]: null }));
      toast.success(`Logo ${slot} removida`);
    } catch {
      toast.error('Erro ao remover logo');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Meu Perfil</h1>
        <p className="text-gray-500 mt-1">Gerencie seus dados pessoais e profissionais</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Card: Dados pessoais */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" /> Dados Pessoais e Profissionais
          </h2>
          <form onSubmit={handleSalvarDados} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" name="nome" value={form.nome} onChange={handleChange} required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="email" value={form.email} disabled
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-100 rounded-xl bg-gray-50 text-gray-400 text-sm cursor-not-allowed" />
              </div>
              <p className="text-xs text-gray-400 mt-1">O email não pode ser alterado</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CREFITO <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" name="crefito" value={form.crefito} onChange={handleChange} placeholder="Ex: 3-12345-F"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Usado nos documentos e PDFs gerados</p>
            </div>
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-2.5 rounded-xl font-medium text-sm transition-colors">
              <Save className="w-4 h-4" />
              {loading ? 'Salvando...' : 'Salvar dados'}
            </button>
          </form>
        </div>

        {/* Card: Alterar senha */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" /> Alterar Senha
          </h2>
          <form onSubmit={handleAlterarSenha} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha atual</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="password" name="senhaAtual" value={form.senhaAtual} onChange={handleChange} required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="password" name="novaSenha" value={form.novaSenha} onChange={handleChange} required minLength={6} placeholder="Mínimo 6 caracteres"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nova senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="password" name="confirmarSenha" value={form.confirmarSenha} onChange={handleChange} required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-2.5 rounded-xl font-medium text-sm transition-colors">
              <Lock className="w-4 h-4" />
              {loading ? 'Alterando...' : 'Alterar senha'}
            </button>
          </form>
        </div>

        {/* Card: Identidade Visual */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" /> Identidade Visual
          </h2>
          <p className="text-xs text-gray-400 mb-4">Sua logo e informações aparecerão nos PDFs gerados</p>
          <form onSubmit={handleSalvarIdentidade} className="space-y-4">

            {/* Upload logos */}
            <div className="grid grid-cols-2 gap-3">
              {[1, 2].map((slot) => {
                const url = logos[`logo${slot}Url`];
                const isAtiva = identidade.logoAtiva === slot;
                return (
                  <div key={slot}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-medium text-gray-600">Logo {slot}</label>
                      {url && (
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="logoAtiva" value={slot}
                            checked={isAtiva}
                            onChange={() => setIdentidade(p => ({ ...p, logoAtiva: slot }))}
                            className="accent-blue-600" />
                          <span className={`text-xs font-medium ${isAtiva ? 'text-blue-600' : 'text-gray-400'}`}>
                            {isAtiva ? 'Ativa nos PDFs' : 'Usar nos PDFs'}
                          </span>
                        </label>
                      )}
                    </div>
                    <div className={`relative border border-dashed rounded-xl overflow-hidden h-20 flex items-center justify-center bg-gray-50 transition-colors ${isAtiva && url ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                      {url ? (
                        <>
                          <img src={url} alt={`Logo ${slot}`} className="h-16 object-contain px-2" />
                          <button type="button" onClick={() => handleDeleteLogo(slot)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition-colors">
                            <X className="w-3 h-3 text-red-500" />
                          </button>
                        </>
                      ) : (
                        <label className="cursor-pointer flex flex-col items-center gap-1 text-gray-400 hover:text-blue-500 transition-colors">
                          {uploadingSlot === slot ? (
                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <ImagePlus className="w-6 h-6" />
                          )}
                          <span className="text-xs">{uploadingSlot === slot ? 'Enviando...' : 'Clique para enviar'}</span>
                          <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden"
                            onChange={(e) => handleUploadLogo(slot, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG ou SVG (máx. 2MB)</p>
                  </div>
                );
              })}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nome da clínica ou profissional</label>
              <input type="text" value={identidade.clinicName}
                onChange={(e) => setIdentidade(p => ({ ...p, clinicName: e.target.value }))}
                placeholder="Clínica FisioWeb"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
                <input type="text" value={identidade.clinicPhone}
                  onChange={(e) => setIdentidade(p => ({ ...p, clinicPhone: e.target.value }))}
                  placeholder="(00) 99999-9999"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input type="email" value={identidade.clinicEmail}
                  onChange={(e) => setIdentidade(p => ({ ...p, clinicEmail: e.target.value }))}
                  placeholder="contato@clinica.com.br"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-2.5 rounded-xl font-medium text-sm transition-colors">
              <Save className="w-4 h-4" />
              {loading ? 'Salvando...' : 'Salvar identidade'}
            </button>
          </form>
        </div>

        {/* Card: Estatísticas */}
        <div className="md:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" /> Minhas Estatísticas
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                icon: <Users className="w-5 h-5 text-blue-600" />,
                bg: 'bg-blue-50',
                value: stats?.totalPacientes ?? '—',
                label: 'Total de Pacientes',
                sub: 'Cadastrados',
              },
              {
                icon: <Calendar className="w-5 h-5 text-green-600" />,
                bg: 'bg-green-50',
                value: stats?.totalSessoes ?? '—',
                label: 'Total de Sessões',
                sub: 'Agendadas',
              },
              {
                icon: <Activity className="w-5 h-5 text-purple-600" />,
                bg: 'bg-purple-50',
                value: stats?.sessoesComEvolucao ?? '—',
                label: 'Evoluções',
                sub: 'Registradas',
              },
              {
                icon: <TrendingUp className="w-5 h-5 text-amber-600" />,
                bg: 'bg-amber-50',
                value: stats?.totalSessoes > 0
                  ? `${Math.round((stats.sessoesComEvolucao / stats.totalSessoes) * 100)}%`
                  : '—',
                label: 'Taxa de Evolução',
                sub: 'Sessões com registro',
              },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                  {item.icon}
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                  <p className="text-xs font-medium text-gray-600">{item.label}</p>
                  <p className="text-xs text-gray-400">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}